<!-- Illustrative mockups of the pages students juggle without BetterConnect,
     used in the landing page hero demo. Everything is sized in em, and the
     root font-size is 1% of the container width, so the page scales like a
     zoomed-out screenshot. -->
<template>
  <div class="cm">
    <!-- Session expired sign-in -->
    <div v-if="page === 'login'" class="cm-login">
      <div class="cm-alert">Your session has expired. Please sign in again.</div>
      <div class="cm-login-card">
        <p class="cm-login-school">{{ school }}</p>
        <p class="cm-login-title">Sign in</p>
        <p class="cm-label">Email</p>
        <div class="cm-input"></div>
        <div class="cm-login-btn">Next</div>
        <p class="cm-link">Having trouble signing in?</p>
      </div>
    </div>

    <!-- Dining menu PDF -->
    <div v-else-if="page === 'dining'" class="cm-pdf">
      <div class="cm-pdf-bar">
        <span>this-week.pdf</span>
        <span>1 / 3</span>
        <span>– 100% +</span>
      </div>
      <div class="cm-pdf-sheet">
        <p class="cm-pdf-kicker">Dining Services</p>
        <p class="cm-pdf-title">Week of October 12</p>
        <table class="cm-pdf-table">
          <tr><th></th><th>Entrée</th><th>Sides</th><th>Soup</th></tr>
          <tr v-for="row in menu" :key="row[0]">
            <td>{{ row[0] }}</td><td>{{ row[1] }}</td><td>{{ row[2] }}</td><td>{{ row[3] }}</td>
          </tr>
        </table>
        <p class="cm-pdf-note">Menu subject to change. Please see a staff member about allergens.</p>
      </div>
    </div>

    <!-- Connect pages -->
    <template v-else>
      <div class="cm-top">
        <span class="cm-school">{{ school }}</span>
        <span class="cm-top-right">
          <span>GMail</span>
          <span><b class="cm-badge">0</b> Official Notes</span>
          <span class="cm-avatar"></span>
          <span class="cm-user">Alex<br>Rivera</span>
        </span>
      </div>
      <div class="cm-nav">
        <span
          v-for="item in navItems"
          :key="item.label"
          class="cm-nav-item"
          :class="{ 'is-active': item.label === activeNav }"
        >
          <span class="cm-ico" :style="{ background: item.color }"></span>
          {{ item.label }}<span v-if="item.menu" class="cm-caret">▾</span>
        </span>
      </div>
      <div v-if="activeNav === 'My Day'" class="cm-subnav">
        <span
          v-for="item in myDayItems"
          :key="item"
          class="cm-subnav-item"
          :class="{ 'is-active': (page === 'assignments' && item === 'Assignment Center') || (page === 'schedule' && item === 'Schedule') }"
        >{{ item }}</span>
      </div>

      <!-- Assignment Center -->
      <div v-if="page === 'assignments'" class="cm-body">
        <div class="cm-panel">
          <p class="cm-week-title">Oct 11 - 17, 2026</p>
          <div class="cm-toolbar">
            <span class="cm-btns"><b>&lt;</b><b>&gt;</b><b class="on">Today</b><b>Day</b><b class="on">Week</b><b>Month</b></span>
            <span class="cm-btns"><b>List</b><b>Print</b></span>
          </div>
          <div class="cm-toolbar">
            <span class="cm-btns"><b class="on">Assigned</b><b>Active</b><b>Due</b></span>
            <span class="cm-btns"><b>+ Add task</b><b>Filter Status</b><b>Filter Classes</b></span>
          </div>
          <div class="cm-week">
            <div v-for="(day, d) in weekDays" :key="day" class="cm-week-col" :class="{ 'is-today': d === 2 }">
              <p class="cm-week-head">{{ day }}</p>
              <div v-for="task in weekTasks.filter((t) => t.day === d)" :key="task.title" class="cm-task">
                <p class="cm-task-title">{{ task.title }}</p>
                <p>{{ task.cls }}</p>
                <p class="cm-task-due">Due: {{ task.due }}</p>
                <span class="cm-pill">To Do</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Schedule -->
      <div v-else-if="page === 'schedule'" class="cm-body">
        <div class="cm-panel">
          <p class="cm-panel-title">Student schedule</p>
          <div class="cm-toolbar">
            <span class="cm-date"><span class="cm-btns"><b>◀</b><b>▶</b></span> Tuesday, October 13, 2026</span>
            <span class="cm-btns"><b>today</b><b>month</b><b>week</b></span>
          </div>
          <div class="cm-info">Tuesday Week 2 (U)</div>
          <table class="cm-table">
            <tr><th>Time</th><th>Block</th><th>Activity</th><th>Contact</th><th>Attendance</th></tr>
            <tr v-for="row in scheduleRows" :key="row[0]">
              <td>{{ row[0] }}</td><td>{{ row[1] }}</td><td class="cm-link">{{ row[2] }}</td><td>{{ row[3] }}</td><td>N/A</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Class page -->
      <div v-else-if="page === 'class'" class="cm-body">
        <div class="cm-panel cm-class-head">
          <span>Advanced Biology - F (F1)</span>
          <span class="cm-btns"><b>View details ▾</b></span>
        </div>
        <div class="cm-tabs">
          <span class="is-active">Bulletin board</span><span>Topics</span><span>Assignments</span>
        </div>
        <div class="cm-class-grid">
          <div class="cm-panel">
            <p class="cm-panel-title">Advanced Biology</p>
            <div class="cm-class-intro">
              <div class="cm-photo"></div>
              <p>
                In this course, you will experience biology as a process, not just a collection of facts.
                Review the unit materials before each lab and bring your notebook to every class.
              </p>
            </div>
            <p class="cm-para">
                Homework is posted under Topics. Check the Assignments tab for due dates, and the
                Downloads section for the course plan and review packets.
            </p>
            <div class="cm-lines"><span style="width: 92%"></span><span style="width: 85%"></span><span style="width: 60%"></span></div>
          </div>
          <div class="cm-side">
            <div class="cm-panel"><p class="cm-side-title">Syllabus</p><p class="cm-link">Syllabus (2026-27)</p></div>
            <div class="cm-panel">
              <p class="cm-side-title">Downloads</p>
              <p class="cm-link">Review Material (REQUIRED)</p>
              <p class="cm-link">Course Plan</p>
              <p class="cm-link">Lab Safety Contract</p>
            </div>
            <div class="cm-panel">
              <p class="cm-side-title">Links</p>
              <p class="cm-link">Unit 2 Practice Quiz</p>
              <p class="cm-link">Class Textbook</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar -->
      <div v-else-if="page === 'calendar'" class="cm-cal">
        <div class="cm-cal-side">
          <p class="cm-cal-view">My View</p>
          <div class="cm-cal-btns"><span>Restore</span><span>Save</span></div>
          <p class="cm-cal-group">My Calendars</p>
          <p v-for="cal in calendars" :key="cal.label" class="cm-cal-item">
            <span class="cm-check" :style="{ background: cal.color }"></span>{{ cal.label }}
          </p>
        </div>
        <div class="cm-cal-main">
          <div class="cm-cal-bar">
            <span class="cm-cal-seg"><b>Today</b><b>Day</b><b>Week</b><b class="on">Month</b></span>
            <span class="cm-cal-month">‹ &nbsp;October 2026&nbsp; ›</span>
            <span class="cm-cal-seg"><b>List</b><b>Print</b></span>
          </div>
          <div class="cm-cal-grid">
            <p v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" :key="day" class="cm-cal-dow">{{ day }}</p>
            <div v-for="(cell, i) in calendarCells" :key="i" class="cm-cal-cell">
              <p class="cm-cal-num">{{ cell.date }}</p>
              <p v-for="(ev, j) in cell.events" :key="j" class="cm-ev" :class="`cm-ev-${ev[0]}`">{{ ev[1] }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  page: { type: String, required: true },
  school: { type: String, default: 'Collegiate School' },
});

const navItems = [
  { label: 'My Day', color: '#f2f2f2', menu: true },
  { label: 'Classes', color: '#6b8e3a', menu: true },
  { label: 'Groups', color: '#d9893b', menu: true },
  { label: 'Resources', color: '#7b9ccc' },
  { label: 'News', color: '#9a9a9a', menu: true },
  { label: 'Calendar', color: '#4a7bd0' },
  { label: 'Directories', color: '#8a5a2b', menu: true },
];

const myDayItems = ['Progress', 'Schedule', 'Assignment Center', 'Course Requests', 'Conduct', 'Requirements'];

const activeNav = computed(() => ({
  assignments: 'My Day',
  schedule: 'My Day',
  class: 'Classes',
  calendar: 'Calendar',
}[props.page]));

const weekDays = ['Sun 10/11', 'Mon 10/12', 'Tue 10/13', 'Wed 10/14', 'Thu 10/15', 'Fri 10/16', 'Sat 10/17'];

const weekTasks = [
  { day: 1, title: 'HW 2.1 Reading', cls: 'Junior English - G', due: 'Tue, Oct 13' },
  { day: 2, title: 'Major: Contrôle de grammaire', cls: 'Advanced French - C', due: 'Thu, Oct 15' },
  { day: 2, title: 'Problem Set 4', cls: 'Precalculus - A', due: 'Wed, Oct 14' },
  { day: 3, title: 'Study Guide 2: Carbs & Lipids', cls: 'Advanced Biology - F', due: 'Fri, Oct 16' },
  { day: 4, title: 'Lab Write-up', cls: 'Machine Learning - D', due: 'Mon, Oct 19' },
  { day: 5, title: 'Response Paper', cls: 'US History - E', due: 'Mon, Oct 19' },
];

const scheduleRows = [
  ['8:20 AM - 9:10 AM', 'F', 'Advanced Biology - F', 'Dr. Patel'],
  ['9:15 AM - 10:05 AM', 'A', 'Precalculus - A', 'Mr. Owens'],
  ['10:10 AM - 10:40 AM', 'Advisory', 'US Advisory - 11', 'Ms. Lee'],
  ['10:45 AM - 11:35 AM', 'C', 'Advanced French - C', 'Mme. Laurent'],
  ['12:00 PM - 12:50 PM', 'G', 'Junior English - G', 'Ms. Brooks'],
  ['12:55 PM - 1:30 PM', 'Lunch', 'US Lunch - 11', 'Staff'],
  ['1:35 PM - 2:25 PM', 'D', 'Machine Learning - D', 'Mr. Chen'],
];

const calendars = [
  { label: 'Assignments', color: '#a9c8ef' },
  { label: 'Schedule', color: '#4cc98a' },
  { label: 'Games/Practices', color: '#f07c2e' },
  { label: 'Class Events', color: '#b49ae6' },
  { label: 'Group Events', color: '#d0605a' },
];

// Three busy weeks of month view: assignments (a), classes (s), games (g).
const dayEvents = [
  [['a', 'HW 1.6 on Showbie'], ['s', 'Monday Week 2 (U)'], ['s', '8:20a Advanced Biology'], ['s', '10:10a US Advisory'], ['s', '10:45a Machine Learning'], ['s', '12:00p Junior English'], ['s', '12:55p US Lunch - 11'], ['s', '1:35p US History'], ['g', '4:30p Soccer - Varsity']],
  [['a', 'Chi-square Practice'], ['a', 'HW 1.7 on Showbie'], ['s', 'Tuesday Week 2 (U)'], ['s', '9:15a Upper School Assembly'], ['s', '10:10a Seminars/Clubs'], ['s', '10:45a Precalculus'], ['s', '12:00p Advanced French'], ['s', '12:55p US Lunch - 11'], ['s', '2:30p Junior English']],
  [['a', 'Les usages de l’infinitif'], ['a', 'HW 1.8 on Showbie'], ['s', 'Wednesday (U)'], ['s', '8:20a Precalculus'], ['s', '9:15a Advanced French'], ['s', '10:30a Machine Learning'], ['s', '12:10p Advanced Biology'], ['s', '12:55p US Lunch - 11'], ['s', '1:40p Group Dynamics']],
  [['a', 'Lab notebook check'], ['s', 'Thursday Week 2 (U)'], ['s', '9:15a Machine Learning'], ['s', '9:15a MS Assembly'], ['s', '10:10a Class Meetings'], ['s', '12:00p Junior English'], ['s', '12:55p US Lunch - 11'], ['s', '1:35p Advanced Biology'], ['s', '2:30p Advanced French']],
  [['a', 'Le participe présent'], ['a', 'HW 1.9 on DM'], ['s', 'Friday Week 2 (U)'], ['s', '8:20a Junior English'], ['s', '10:10a Advanced French'], ['s', '11:05a Precalculus'], ['s', '12:45p US Lunch - 11'], ['s', '1:30p US History'], ['g', '4:30p Soccer - Varsity']],
];

const calendarCells = computed(() => {
  const cells = [];
  const starts = [27, 4, 11];
  starts.forEach((start, week) => {
    for (let d = 0; d < 7; d += 1) {
      const raw = start + d;
      const date = week === 0 ? (raw > 30 ? raw - 30 : raw) : raw;
      const isWeekday = d >= 1 && d <= 5;
      cells.push({
        date,
        events: isWeekday ? dayEvents[(d - 1 + week) % 5] : [],
      });
    }
  });
  return cells;
});

const menu = [
  ['Mon', 'Chicken parmesan', 'Caesar salad, garlic bread', 'Minestrone'],
  ['Tue', 'Beef tacos', 'Black beans, rice, salsa', 'Tortilla soup'],
  ['Wed', 'Teriyaki salmon', 'Jasmine rice, bok choy', 'Miso'],
  ['Thu', 'Penne alla vodka', 'Roasted broccoli', 'Tomato basil'],
  ['Fri', 'Cheeseburgers', 'Sweet potato fries', 'Chicken noodle'],
];
</script>

<style scoped>
.cm {
  /* 1em = 1% of the mock's width */
  font-size: 1cqw;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #eeeeee;
  color: #333;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.35;
  text-align: left;
}
.cm p {
  margin: 0;
}

/* Connect chrome */
.cm-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 3.4em;
  padding: 0 14%;
  background: #0b2265;
  color: #fff;
}
.cm-school {
  font-size: 1.7em;
  font-weight: 300;
}
.cm-top-right {
  display: flex;
  align-items: center;
  gap: 1.4em;
  font-size: 0.85em;
}
.cm-badge {
  background: #111;
  padding: 0.1em 0.45em;
  border-radius: 0.2em;
  margin-right: 0.3em;
}
.cm-avatar {
  width: 1.9em;
  height: 1.9em;
  border-radius: 50%;
  background: #c9a98a;
  margin-right: -0.9em;
}
.cm-user {
  font-size: 0.85em;
  line-height: 1.05;
}
.cm-nav {
  display: flex;
  gap: 2.2em;
  height: 4em;
  align-items: center;
  padding: 0 14%;
  background: #e6e6e6;
  border-bottom: 1px solid #d0d0d0;
  font-size: 1.15em;
  color: #0b2265;
}
.cm-nav-item {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.4em;
  height: 100%;
  padding: 0 0.5em;
}
.cm-nav-item.is-active {
  background: #d2d2d2;
}
.cm-ico {
  width: 1.5em;
  height: 1.5em;
  border-radius: 0.2em;
  box-shadow: inset 0 0 0 0.1em rgba(0, 0, 0, 0.15);
}
.cm-caret {
  font-size: 0.7em;
}
.cm-subnav {
  display: flex;
  gap: 1.6em;
  align-items: center;
  height: 3.6em;
  padding: 0 15%;
  background: #e9e9e9;
  box-shadow: 0 0.3em 0.6em rgba(0, 0, 0, 0.12);
  font-size: 1.05em;
  color: #555;
}
.cm-subnav-item.is-active {
  background: #d6d6d6;
  border: 1px solid #bbb;
  border-radius: 0.2em;
  padding: 0.3em 0.6em;
  color: #111;
  font-weight: 600;
}

.cm-body {
  padding: 2.2em 14%;
}
.cm-panel {
  background: #fff;
  padding: 1.6em;
  border-top: 0.3em solid #d5d6dc;
}
.cm-panel-title {
  font-size: 1.9em;
  font-weight: 300;
  margin-bottom: 0.6em !important;
}
.cm-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5em 0;
  border-bottom: 1px solid #eee;
}
.cm-btns {
  display: inline-flex;
  gap: 0.4em;
}
.cm-btns b {
  font-weight: 400;
  font-size: 1em;
  padding: 0.35em 0.7em;
  border: 1px solid #ccc;
  border-radius: 0.25em;
  background: #fff;
}
.cm-btns b.on {
  background: #ddd;
}
.cm-link {
  color: #1673b5;
  font-weight: 600;
}

/* Assignment center */
.cm-week-title {
  text-align: center;
  font-size: 2em;
  margin-bottom: 0.4em !important;
}
.cm-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid #ddd;
  margin-top: 1em;
  min-height: 18em;
}
.cm-week-col {
  border-right: 1px solid #ddd;
  padding-bottom: 0.4em;
}
.cm-week-col.is-today {
  background: #fdf6dc;
}
.cm-week-head {
  text-align: center;
  font-weight: 700;
  color: #1673b5;
  border-bottom: 1px solid #ddd;
  padding: 0.3em 0;
  font-size: 1.05em;
}
.cm-task {
  margin: 0.3em;
  padding: 0.4em;
  background: #d3eef9;
  font-size: 0.85em;
}
.cm-task-title {
  color: #1673b5;
}
.cm-task-due {
  color: #b52a2a;
}
.cm-pill {
  display: inline-block;
  margin-top: 0.4em;
  padding: 0.2em 0.7em;
  border-radius: 1em;
  background: #b9a6e0;
  color: #fff;
}

/* Schedule */
.cm-date {
  display: flex;
  align-items: center;
  gap: 0.8em;
  font-size: 1.6em;
}
.cm-date .cm-btns {
  font-size: 0.6em;
}
.cm-info {
  margin: 0.8em 0;
  padding: 0.7em 1em;
  background: #82d2f2;
  border-radius: 0.2em;
  font-size: 1.05em;
}
.cm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.05em;
}
.cm-table th {
  text-align: left;
  font-weight: 400;
  color: #777;
  background: #eee;
  padding: 0.6em 0.5em;
}
.cm-table td {
  padding: 0.6em 0.5em;
  border-bottom: 1px dashed #e2e2e2;
}

/* Class page */
.cm-class-head {
  display: flex;
  align-items: center;
  gap: 1.2em;
  font-size: 2em;
  font-weight: 300;
  padding: 0.6em 0.7em;
  border-top: 0;
}
.cm-class-head .cm-btns {
  font-size: 0.4em;
  font-weight: 600;
}
.cm-tabs {
  display: flex;
  gap: 1.6em;
  margin: 1.4em 0 1em;
  border-bottom: 0.15em solid #1673b5;
  font-size: 1.1em;
  font-weight: 600;
  color: #666;
}
.cm-tabs span {
  padding: 0.5em 0.8em;
}
.cm-tabs .is-active {
  background: #1673b5;
  color: #fff;
}
.cm-class-grid {
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 2em;
}
.cm-class-intro {
  display: flex;
  gap: 1em;
  margin-bottom: 1em;
  font-size: 1.05em;
}
.cm-photo {
  flex-shrink: 0;
  width: 14em;
  height: 10em;
  background: linear-gradient(135deg, #8aa1b1, #c9d3da 55%, #6f7f8a);
}
.cm-para {
  font-size: 1.05em;
  margin-bottom: 1em !important;
}
.cm-lines {
  display: flex;
  flex-direction: column;
  gap: 0.7em;
}
.cm-lines span {
  height: 0.6em;
  background: #e3e3e3;
}
.cm-side {
  display: flex;
  flex-direction: column;
  gap: 1.2em;
}
.cm-side .cm-panel {
  border-top: 0;
  font-size: 0.95em;
}
.cm-side .cm-link {
  padding: 0.5em 0;
  border-bottom: 1px solid #eee;
}
.cm-side-title {
  font-size: 1.5em;
  font-weight: 700;
  margin-bottom: 0.3em !important;
}

/* Calendar */
.cm-cal {
  display: grid;
  grid-template-columns: 14% 1fr;
  gap: 1.2em;
  padding: 2.6em 1.2em 0 1em;
}
.cm-cal-side {
  font-size: 1.05em;
}
.cm-cal-view {
  text-align: center;
  font-size: 1.1em;
}
.cm-cal-btns {
  display: flex;
  gap: 0.5em;
  margin: 0.3em 0 0.9em;
}
.cm-cal-btns span {
  flex: 1;
  text-align: center;
  padding: 0.35em 0;
  background: #1c3170;
  color: #fff;
  border-radius: 0.2em;
}
.cm-cal-group {
  font-size: 1.15em;
  margin-bottom: 0.4em !important;
}
.cm-cal-item {
  display: flex;
  align-items: center;
  gap: 0.6em;
  padding: 0.55em 0;
  border-bottom: 1px solid #ddd;
}
.cm-check {
  width: 1.2em;
  height: 1.2em;
  border-radius: 0.2em;
}
.cm-cal-main {
  background: #fff;
  padding: 1em;
}
.cm-cal-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #2c2c2c;
  color: #fff;
  padding: 0.4em;
}
.cm-cal-seg {
  display: inline-flex;
}
.cm-cal-seg b {
  font-weight: 400;
  padding: 0.35em 0.8em;
  background: #3a3a3a;
}
.cm-cal-seg b.on {
  background: #1b1b1b;
}
.cm-cal-month {
  font-size: 1.9em;
  font-weight: 300;
}
.cm-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.cm-cal-dow {
  text-align: center;
  font-size: 1.15em;
  padding: 0.3em 0;
  border-bottom: 1px solid #ddd;
}
.cm-cal-cell {
  border-right: 1px solid #e3e3e3;
  border-bottom: 1px solid #e3e3e3;
  padding: 0.2em 0.15em;
  min-height: 8em;
}
.cm-cal-num {
  text-align: right;
  font-size: 1.1em;
  color: #777;
  padding-right: 0.2em;
}
.cm-ev {
  font-size: 0.8em;
  padding: 0.12em 0.3em;
  margin-bottom: 0.15em !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cm-ev-a {
  background: #a9c8ef;
}
.cm-ev-s {
  background: #4cc98a;
}
.cm-ev-g {
  background: #f07c2e;
  color: #fff;
}

/* Sign in */
.cm-login {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 5em;
  background: #f4f5f7;
}
.cm-alert {
  font-size: 1.3em;
  padding: 0.7em 1.4em;
  margin-bottom: 2em;
  background: #fff4d4;
  border: 1px solid #f1d27a;
  border-radius: 0.3em;
  color: #6b5200;
}
.cm-login-card {
  width: 46em;
  padding: 3em;
  background: #fff;
  border-radius: 0.6em;
  box-shadow: 0 0.3em 1.2em rgba(0, 0, 0, 0.08);
}
.cm-login-school {
  font-size: 1.3em;
  color: #0b2265;
  font-weight: 600;
}
.cm-login-title {
  font-size: 3em;
  font-weight: 600;
  margin: 0.2em 0 0.6em !important;
}
.cm-label {
  font-size: 1.3em;
  font-weight: 600;
  margin-bottom: 0.4em !important;
}
.cm-input {
  height: 4em;
  border: 1px solid #b8bec7;
  border-radius: 0.4em;
}
.cm-login-btn {
  margin: 2em 0 1.6em;
  width: 10em;
  padding: 1em 0;
  text-align: center;
  font-size: 1.3em;
  font-weight: 600;
  color: #fff;
  background: #0974a6;
  border-radius: 0.4em;
}
.cm-login .cm-link {
  font-size: 1.2em;
}

/* Dining PDF */
.cm-pdf {
  height: 100%;
  background: #525659;
}
.cm-pdf-bar {
  display: flex;
  justify-content: space-between;
  padding: 1em 2em;
  background: #323639;
  color: #f1f1f1;
  font-size: 1.3em;
}
.cm-pdf-sheet {
  width: 64%;
  margin: 2.5em auto 0;
  padding: 4em 4em 6em;
  background: #fff;
  box-shadow: 0 0.4em 1.5em rgba(0, 0, 0, 0.4);
  font-family: Georgia, 'Times New Roman', serif;
}
.cm-pdf-kicker {
  font-size: 1.3em;
  color: #8a6d3b;
}
.cm-pdf-title {
  font-size: 3em;
  margin-bottom: 0.8em !important;
}
.cm-pdf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.15em;
}
.cm-pdf-table th {
  text-align: left;
  border-bottom: 0.15em solid #333;
  padding: 0.4em;
}
.cm-pdf-table td {
  border-bottom: 1px solid #ddd;
  padding: 0.6em 0.4em;
  vertical-align: top;
}
.cm-pdf-note {
  margin-top: 1.6em !important;
  font-size: 1em;
  font-style: italic;
  color: #777;
}
</style>
